import {ActionIcon, Divider, Paper, Text, Tooltip, createStyles, rem} from '@mantine/core';
import React from 'react';

import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

export interface ZoomControlStripButton {
  key: string;
  icon: BenchlingIconName | React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}

export interface ZoomControlStripProps {
  zoomLevelLabel: string;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onFitToScreen?: () => void;
  onReset?: () => void;
  zoomInDisabled?: boolean;
  zoomOutDisabled?: boolean;
  fitDisabled?: boolean;
  centerDisabled?: boolean;
  additionalButtons?: Array<ZoomControlStripButton>;
  className?: string;
  style?: React.CSSProperties;
}

const CONTROL_BUTTON_SIZE = rem(28);

const useStyles = createStyles((theme) => ({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: theme.white,
    borderRadius: theme.radius.md,
    padding: `${rem(2)} ${rem(6)}`,
    gap: rem(4),
  },
  zoomSection: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: rem(4),
    paddingRight: rem(4),
    borderRight: `1px solid ${theme.colors.gray[2]}`,
  },
  zoomLevelLabel: {
    width: rem(56),
    textAlign: 'center',
    fontWeight: 500,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.gray[7],
    lineHeight: 1.1,
  },
  trailingButtons: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: rem(4),
  },
  controlButton: {
    width: CONTROL_BUTTON_SIZE,
    height: CONTROL_BUTTON_SIZE,
    color: theme.colors.gray[7],
    border: 'none',
    backgroundColor: theme.white,
    '&:disabled': {
      backgroundColor: theme.white,
    },
  },
}));

export default function ZoomControlStrip({
  zoomLevelLabel,
  onZoomIn,
  onZoomOut,
  onFitToScreen,
  onReset,
  zoomInDisabled,
  zoomOutDisabled,
  fitDisabled,
  centerDisabled,
  additionalButtons,
  className,
  style,
}: ZoomControlStripProps): JSX.Element {
  const {classes, cx} = useStyles();
  const trailingButtons: Array<React.ReactNode> = [];

  if (onFitToScreen != null) {
    trailingButtons.push(
      <ZoomControlButton
        key="fit-to-screen"
        icon="square-corners"
        label="Fit to screen"
        onClick={onFitToScreen}
        disabled={fitDisabled}
      />
    );
  }

  if (onFitToScreen != null && onReset != null) {
    trailingButtons.push(<Divider key="fit-center-divider" orientation="vertical" />);
  }

  if (onReset != null) {
    trailingButtons.push(
      <ZoomControlButton
        key="reset-position"
        icon="detect"
        label="Reset position"
        onClick={onReset}
        disabled={centerDisabled}
      />
    );
  }

  additionalButtons?.forEach((button) => {
    trailingButtons.push(
      <ZoomControlButton
        key={button.key}
        icon={button.icon}
        label={button.label}
        onClick={button.onClick}
        disabled={button.disabled}
      />
    );
  });

  return (
    <Paper
      className={cx(classes.root, className)}
      withBorder={true}
      radius="sm"
      shadow="xs"
      style={style}
      role="group"
      aria-label="Zoom controls"
    >
      <div className={classes.zoomSection}>
        <ZoomControlButton
          icon="minus-large"
          label="Zoom out"
          onClick={onZoomOut}
          disabled={zoomOutDisabled || onZoomOut == null}
        />
        <Text className={classes.zoomLevelLabel}>{zoomLevelLabel}</Text>
        <ZoomControlButton
          icon="plus-large"
          label="Zoom in"
          onClick={onZoomIn}
          disabled={zoomInDisabled || onZoomIn == null}
        />
      </div>
      {trailingButtons.length > 0 && <div className={classes.trailingButtons}>{trailingButtons}</div>}
    </Paper>
  );
}

interface ZoomControlButtonProps {
  icon: BenchlingIconName | React.ReactNode;
  label: string;
  disabled?: boolean;
  onClick?: () => void;
}

function ZoomControlButton({icon, label, disabled, onClick}: ZoomControlButtonProps): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  const content = typeof icon === 'string' ? <BenchlingIcon icon={icon as BenchlingIconName} /> : icon;

  return (
    <Tooltip label={label}>
      <ActionIcon
        aria-label={label}
        variant="light"
        size="md"
        radius="md"
        color="gray"
        onClick={onClick}
        disabled={disabled || onClick == null}
        className={classes.controlButton}
      >
        {content}
      </ActionIcon>
    </Tooltip>
  );
}
